import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useOnboardingStore, getTourSteps, type TourRole } from '@/stores/onboarding';
import { useAuthStore } from '@/stores/auth';
import { Button } from './Button';

export function OnboardingTour() {
  const isActive = useOnboardingStore((s) => s.isActive);
  const currentStep = useOnboardingStore((s) => s.currentStep);
  const next = useOnboardingStore((s) => s.next);
  const skip = useOnboardingStore((s) => s.skip);

  // Get the logged-in user's role so we show role-appropriate tour steps
  const role = useAuthStore((s) => s.employee?.role) as TourRole | undefined;
  const steps = getTourSteps(role);

  // Null guard — if steps array is somehow empty, bail out silently
  const step = steps[currentStep] ?? null;

  const isLast = currentStep === steps.length - 1;
  const progress = ((currentStep + 1) / steps.length) * 100;

  // Keyboard navigation: Escape → skip, Enter/ArrowRight → next
  useEffect(() => {
    if (!isActive) return;

    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        skip();
      } else if (e.key === 'Enter' || e.key === 'ArrowRight') {
        next(role);
      }
    }

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isActive, next, skip, role]);

  if (!isActive || !step) return null;

  /*
   * Render into document.body via a portal so this component is completely
   * outside any parent stacking context. This eliminates z-index conflicts
   * with other fixed/sticky elements in the app layout.
   */
  return createPortal(
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 z-[10000] animate-[fadeIn_0.3s_ease-out]"
        aria-hidden="true"
      />

      {/* Tour card — sits above the backdrop */}
      <div
        className="fixed inset-0 z-[10001] flex items-center justify-center p-6"
        role="dialog"
        aria-modal="true"
        aria-labelledby="onboarding-title"
        aria-describedby="onboarding-description"
      >
        <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full overflow-hidden animate-[loginSlideUp_0.4s_ease-out]">
          {/* Progress bar */}
          <div className="h-1 bg-gray-100" aria-hidden="true">
            <div
              className="h-full bg-brand-blue-600 transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>

          <div className="p-6 text-center">
            {/* Icon */}
            <div className="text-4xl mb-4" aria-hidden="true">{step.icon}</div>

            {/* Content */}
            <h3 id="onboarding-title" className="text-lg font-bold text-gray-900 mb-2">
              {step.title}
            </h3>
            <p id="onboarding-description" className="text-sm text-gray-500 leading-relaxed">
              {step.message}
            </p>

            {/* Step indicator dots */}
            <div className="flex items-center justify-center gap-1.5 mt-5 mb-4" aria-hidden="true">
              {steps.map((_, i) => (
                <div
                  key={i}
                  className={`h-1.5 rounded-full transition-all duration-300 ${
                    i === currentStep
                      ? 'w-6 bg-brand-blue-600'
                      : i < currentStep
                      ? 'w-1.5 bg-brand-blue-300'
                      : 'w-1.5 bg-gray-200'
                  }`}
                />
              ))}
            </div>

            {/* Step counter for screen readers */}
            <p className="sr-only">
              Step {currentStep + 1} of {steps.length}
            </p>

            {/* Actions */}
            <div className="flex items-center justify-between gap-3">
              <button
                onClick={skip}
                className="text-sm text-gray-400 hover:text-gray-600 transition-colors px-3 py-2"
              >
                Skip tour
              </button>
              <Button onClick={() => next(role)} size="sm">
                {isLast ? 'Get Started' : 'Next'}
                {!isLast && (
                  <svg
                    className="w-4 h-4 ml-1"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    aria-hidden="true"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </>,
    document.body,
  );
}
